"use client";

import { format } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { enUS as enDayPicker, ptBR as ptDayPicker } from "react-day-picker/locale";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const localeConfig = {
  en: {
    dateFns: enUS,
    dayPicker: enDayPicker,
    formatPattern: "PPP p" as const,
  },
  "pt-BR": {
    dateFns: ptBR,
    dayPicker: ptDayPicker,
    formatPattern: "PPP p" as const,
  },
} as const;

export type DateTimePickerLocale = keyof typeof localeConfig;

const parseDatetimeLocal = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const toDatetimeLocal = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toTimeValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const applyTime = (date: Date, time: string): Date => {
  const [hours, minutes] = time.split(":").map(Number);
  const next = new Date(date);
  next.setHours(hours ?? 0, minutes ?? 0, 0, 0);

  return next;
};

export type DateTimePickerProps = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
  id?: string;
  disabled?: boolean;
  "aria-invalid"?: boolean;
  placeholder?: string;
  locale?: DateTimePickerLocale;
};

export const DateTimePicker = ({
  value,
  onChange,
  onBlur,
  id,
  disabled,
  "aria-invalid": ariaInvalid,
  placeholder = "Pick date and time",
  locale = "en",
}: DateTimePickerProps) => {
  const [open, setOpen] = React.useState(false);
  const selected = parseDatetimeLocal(value);
  const { dateFns, dayPicker, formatPattern } = localeConfig[locale];

  const update = (date: Date) => {
    onChange?.(toDatetimeLocal(date));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PopoverTrigger
        id={id}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        render={
          <Button
            variant="outline"
            data-empty={!selected}
            className={cn("w-full justify-start text-left data-[empty=true]:text-muted-foreground")}
          >
            <CalendarIcon />
            {selected ? (
              format(selected, formatPattern, { locale: dateFns })
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        }
      />
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={dayPicker}
          selected={selected}
          defaultMonth={selected}
          onSelect={(day) => {
            if (!day) return;
            const base = selected ?? new Date();
            update(applyTime(day, toTimeValue(base)));
          }}
        />
        <div className="border-t border-border p-3">
          <Input
            type="time"
            value={selected ? toTimeValue(selected) : ""}
            onChange={(e) => {
              const time = e.target.value;
              if (!time) return;
              const base = selected ?? new Date();
              update(applyTime(base, time));
            }}
            className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
            aria-label="Time"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
